import { List } from "immutable";
import { BrandRecord as Brand } from "records/brand";
import { FDMCompanyRecordClass, TFDMCompanyRecord } from "records/company";
import { handleActions } from "redux-actions";
import {
    BRAND_ADMIN_UPDATE_PERMISSIONS_COMPLETE,
    BRAND_GENERIC_UPDATE_COMPLETE,
    BRAND_GROUPS_UPDATE,
    BRAND_GROUPS_UPDATE_COMPLETED,
    BRAND_GUEST_BUYER_WHITELIST_PRODUCT_VALUES_UPDATE,
    BRAND_GUEST_BUYER_WHITELIST_PRODUCT_VALUES_UPDATE_COMPLETED,
    BRAND_PREFERENCES_UPDATE_COMPLETE,
    BRAND_SPLIT_FIELDS_UPDATE_COMPLETE,
    BRAND_WAREHOUSE_COMPLETE,
    BRAND_WAREHOUSE_DELETE_COMPLETE,
    BRAND_WAREHOUSE_UPDATE_COMPLETE,
    BRANDSETTINGS,
    SCOPE,
    SCOPE_BRAND_CLEAR,
    SCOPE_BRAND_SET,
    SCOPE_BRAND_UPDATE,
    SCOPE_COMPANIES_REQUEST,
    SCOPE_COMPANY,
    SCOPE_COMPANY_CLEAR,
    SCOPE_COMPANY_SET,
    SCOPE_CURRENCY_SET,
    SCOPE_HIDE_MSRP,
    SCOPE_HIDE_WHOLESALE,
    SCOPE_IS_CHILD_FRAME_SET,
    SCOPE_PAGE_CLEAR,
    SCOPE_PAGE_SET,
    SCOPE_SET_USER_TYPE,
    SCOPE_SHOW_TERMS,
    SCOPE_SITE_CLEAR,
    SCOPE_SITE_SET,
    SCOPE_SYNC_FROM_CART,
    SCOPE_VIEW_AS,
    SCOPE_VIEW_AS_GUEST,
    BRAND,
    BRAND_ORDER_RECAP_UPDATE,
} from "constants/action-types";
import ajaxConfig from "./../lib/ajax";
import me from "models/me";
import Validators from "lib/validators";
import fpSet from "lodash/fp/set";
import { IPage, ISite } from "containers/sites-manager/hooks";
import _ from "lodash";
import { hasCompanyAccess } from "helpers/access-control";
import { ICompany } from "components/contacts/contacts-types";
import { AccountType } from "types/user";

export interface IState {
    availableCurrencies: List<string>;
    availableMsrpCurrencies?: List<string>;
    brand: Brand | null;
    companies: any;
    company: any;
    currencyCode: string;
    msrpCurrencyCode?: string;
    error?: boolean;
    viewAs: any; // company
    viewAsGuest: boolean;
    fetchingMinMaxRules?: boolean;
    preferredRetailers: any[];
    preferredRetailersError: any;
    fetchingPreferredRetailers?: boolean;
    fetchingMasterSchema?: boolean;
    isUpdating?: boolean;
    accountType?: AccountType;
    startOrderCompanies: any;
    fetchingStartOrderCompanies: boolean;
    nextPageStartOrderCompanies: boolean;
    hideMsrp: boolean;
    hideWholesale: boolean;
    page: IPage | null;
    site: ISite | null;
    showTerms?: boolean;
    isChildFrame?: boolean;
    currentMerchantId?: string;
    currentCompanies?: Record<string, TFDMCompanyRecord>;
}

export type IScope = IState;

const initialState: IState = {
    availableCurrencies: List.of(""),
    brand: null,
    companies: [],
    company: null,
    currencyCode: undefined,
    error: undefined,
    viewAs: undefined,
    viewAsGuest: false,
    fetchingMinMaxRules: false,
    preferredRetailers: [],
    preferredRetailersError: null,
    fetchingPreferredRetailers: false,
    fetchingMasterSchema: false,
    isUpdating: false,
    accountType: undefined,
    startOrderCompanies: [],
    fetchingStartOrderCompanies: false,
    nextPageStartOrderCompanies: false,
    hideWholesale: false,
    hideMsrp: false,
    page: null,
    site: null,
    showTerms: false,
    isChildFrame: false,
    currentMerchantId: "fake_id",
    currentCompanies: null,
};

export const getCompanyFromCompanies = (companies: ICompany[], state: IScope) => {
    const useCompanyAccessControl = state.brand?.hasPreference("company_access_control");
    return useCompanyAccessControl ? _.find(companies, (company) => hasCompanyAccess(company)) : _.head(companies);
};

const determineCurrencies = (state: IState): { wholesale: string[]; retail: string[] } => {
    const { brand, company, viewAs } = state;
    if (viewAs) {
        return { wholesale: viewAs.__currency_codes, retail: viewAs.__msrp_currency_codes };
    } else if (company) {
        return { wholesale: company.__currency_codes, retail: company.__msrp_currency_codes };
    }
    if (brand) {
        const currencies = me.hasPermission("view_all_currencies")
            ? brand.settings.currencies
            : [brand._brandConnection.currency_code || ""];
        return { wholesale: currencies, retail: currencies };
    }
    return { wholesale: [""], retail: [""] }; // Fallback
};

type GetChangesFunc = (obj: any, state: IState) => Partial<IState>;

const modifyScope = (getChanges: GetChangesFunc) => {
    return (state: IState, { payload } = { payload: undefined }): IState => {
        const newState: IState = { ...state, ...getChanges(payload, state) };
        const { wholesale, retail } = determineCurrencies(newState);
        if (newState.brand?.hasPreference("separate_msrp_currency")) {
            newState.availableMsrpCurrencies = List(retail);
        }
        const currencies = (newState.availableCurrencies = List(wholesale));
        if (!currencies.includes(newState.currencyCode)) {
            const fallback = newState.brand ? newState.brand._brandConnection.currency_code : "";
            const brandFallback = newState.brand ? newState.brand.settings.default_currency : "";
            if (currencies.includes(fallback)) {
                newState.currencyCode = fallback;
            } else if (currencies.includes(brandFallback)) {
                newState.currencyCode = newState.brand.settings.default_currency;
            } else {
                newState.currencyCode = currencies.first();
            }
        }
        ajaxConfig.scope = newState;
        return newState;
    };
};

export const scope = handleActions<IState, any>(
    {
        [SCOPE_SET_USER_TYPE]: modifyScope((accountType) => ({
            accountType,
        })),
        [SCOPE_BRAND_SET]: modifyScope((payload) => ({
            brand: payload ? new Brand(payload) : null,
        })),

        [SCOPE_BRAND_UPDATE]: modifyScope((payload: Brand) => ({
            brand: payload,
        })),

        [SCOPE_BRAND_CLEAR]: modifyScope(() => ({ brand: null })),

        [SCOPE_COMPANIES_REQUEST]: modifyScope((payload = [], state) => {
            const FDMCompanyRecord = FDMCompanyRecordClass(state.brand?.getSchema("company"));

            return {
                companies: payload.map((company) => new FDMCompanyRecord(company)),
                company: getCompanyFromCompanies(payload, state) || null,
            };
        }),

        [SCOPE_SHOW_TERMS]: modifyScope((payload) => ({
            showTerms: !payload.latest,
        })),

        [SCOPE_COMPANY_SET]: modifyScope((payload = null) => ({
            company: payload,
        })),

        [SCOPE_COMPANY_CLEAR]: modifyScope(() => ({
            company: null,
        })),
        [SCOPE_CURRENCY_SET]: modifyScope((payload = "USD") => ({
            currencyCode: payload,
        })),

        [SCOPE_PAGE_SET]: modifyScope((payload = null) => ({
            page: payload,
        })),

        [SCOPE_PAGE_CLEAR]: modifyScope(() => ({
            page: null,
        })),

        [SCOPE_SITE_SET]: modifyScope((payload = null) => ({
            site: payload,
        })),

        [SCOPE_SITE_CLEAR]: modifyScope(() => ({
            site: null,
        })),

        [SCOPE_VIEW_AS]: modifyScope((payload) => ({
            viewAs: payload,
            viewAsGuest: false,
        })),

        [SCOPE_SYNC_FROM_CART]: modifyScope(({ company, currencyCode, msrpCurrencyCode }) => ({
            company,
            currencyCode,
            msrpCurrencyCode,
            viewAs: company,
            viewAsGuest: false,
        })),

        [SCOPE_VIEW_AS_GUEST]: modifyScope((payload, state) => ({
            company: null,
            viewAs: undefined,
            viewAsGuest: !state.viewAsGuest,
        })),
        [BRAND_WAREHOUSE_COMPLETE]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }

            const currentBrand = state.brand.toJS();
            currentBrand.settings.warehouses = currentBrand.settings.warehouses.concat(payload);
            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            ajaxConfig.scope = newState;
            return newState;
        },
        [BRAND_WAREHOUSE_UPDATE_COMPLETE]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }

            const currentBrand = state.brand.toJS();
            currentBrand.settings.warehouses = currentBrand.settings.warehouses.reduce((accumulator, warehouse) => {
                if (warehouse._id === payload.id) {
                    accumulator = accumulator.concat(payload);
                } else {
                    accumulator = accumulator.concat(warehouse);
                }
                return accumulator;
            }, []);
            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            ajaxConfig.scope = newState;
            return newState;
        },
        [BRAND_PREFERENCES_UPDATE_COMPLETE]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }

            const currentBrand = state.brand.toJS();
            if (payload.settings) {
                currentBrand.settings.preferences = payload.settings.preferences;
                currentBrand.settings.marketplace = payload.settings.marketplace;
                currentBrand.settings.digest_email_list = payload.settings.digest_email_list;
                currentBrand.settings.published_seasons = payload.settings.published_seasons;
                currentBrand.settings.subteams = payload.settings.subteams;
                currentBrand.settings.nuorder_processor_statement_email_list =
                    payload.settings.nuorder_processor_statement_email_list;
                currentBrand.settings.image_formats = payload.settings.image_formats;
                currentBrand.settings.strikethrough = payload.settings.strikethrough;
            }
            if (payload.modules && payload.modules.buyer_marketplace) {
                currentBrand.modules.buyer_marketplace.sales_reps = payload.modules.buyer_marketplace.sales_reps;
            }

            // brand admin
            if (payload.contact_information) {
                currentBrand.contact_information = {
                    ...currentBrand.contact_information,
                    ...payload.contact_information,
                };
            }
            if (payload.name) {
                currentBrand.name = payload.name;
            }

            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            ajaxConfig.scope = newState;

            return newState;
        },
        [BRAND_GROUPS_UPDATE]: (state: IState) => {
            return {
                ...state,
                isUpdating: true,
            };
        },
        [BRAND_GROUPS_UPDATE_COMPLETED]: (state: IState, { payload, error }): IState => {
            if (error) {
                return {
                    ...state,
                    isUpdating: false,
                };
            }

            const currentBrand = state.brand.toJS();
            currentBrand.modules.base.customer_groups = payload.modules.base.customer_groups;
            currentBrand.settings.preferences = payload.settings.preferences;
            const brand = new Brand(currentBrand);
            const newState = { ...state, isUpdating: false, brand };
            ajaxConfig.scope = newState;

            return newState;
        },
        [BRAND_GUEST_BUYER_WHITELIST_PRODUCT_VALUES_UPDATE]: (state: IState) => {
            return {
                ...state,
                isUpdating: true,
            };
        },
        [BRAND_ADMIN_UPDATE_PERMISSIONS_COMPLETE]: (state: IState, { payload, error }): IState => {
            if (error) {
                return {
                    ...state,
                    isUpdating: false,
                };
            }

            const currentBrand = state.brand.toJS();

            if (payload.default_permissions) {
                currentBrand.default_permissions = payload.default_permissions;
            }

            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            ajaxConfig.scope = newState;

            return newState;
        },
        [BRAND_GUEST_BUYER_WHITELIST_PRODUCT_VALUES_UPDATE_COMPLETED]: (state: IState, { payload, error }): IState => {
            if (error) {
                return {
                    ...state,
                    isUpdating: false,
                };
            }

            const currentBrand = state.brand.toJS();
            currentBrand.settings.guest_buyer_product_whitelist_values =
                payload.settings.guest_buyer_product_whitelist_values;
            const brand = new Brand(currentBrand);
            const newState = { ...state, isUpdating: false, brand };
            ajaxConfig.scope = newState;

            return newState;
        },
        [BRAND_SPLIT_FIELDS_UPDATE_COMPLETE]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }

            const currentBrand = state.brand.toJS();
            currentBrand.settings.order_split_fields = payload.settings.order_split_fields;
            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            ajaxConfig.scope = newState;
            return newState;
        },
        [BRAND_ORDER_RECAP_UPDATE.SUCCESS]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }

            const currentBrand = state.brand.toJS();
            currentBrand.settings.order_recap = payload?.settings?.order_recap;
            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            ajaxConfig.scope = newState;
            return newState;
        },
        [BRAND.LEGAL_FILES.FETCH.SUCCESS]: (state: IState, { payload }): IState => {
            const currentBrand = state.brand.toJS();
            currentBrand.modules.base.legal_files = payload;
            const newState = { ...state, brand: new Brand(currentBrand) };
            ajaxConfig.scope = newState;
            return newState;
        },
        [BRAND_GENERIC_UPDATE_COMPLETE]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }
            const currentBrand = state.brand.toJS();
            const brand = new Brand(fpSet(payload.key, payload.body, currentBrand));
            const newState = { ...state, brand };
            ajaxConfig.scope = newState;
            return newState;
        },

        [BRAND_WAREHOUSE_DELETE_COMPLETE]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }

            const currentBrand = state.brand.toJS();
            currentBrand.settings.warehouses = currentBrand.settings.warehouses.filter((warehouse) => {
                return warehouse._id !== payload.id;
            });
            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            ajaxConfig.scope = newState;
            return newState;
        },
        [BRANDSETTINGS.ORDERMINMAX.CREATE.COMPLETED]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }
            const currentBrand = state.brand.toJS();
            currentBrand.settings.order_quantity_rules = payload;
            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            ajaxConfig.scope = newState;
            return newState;
        },
        [BRANDSETTINGS.ORDERMINMAX.UPDATE.COMPLETED]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }
            const currentBrand = state.brand.toJS();
            currentBrand.settings.order_quantity_rules = payload;
            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            // const newState = (Object as any).assign({}, state, { brand });
            ajaxConfig.scope = newState;
            return newState;
        },
        [BRANDSETTINGS.ORDERMINMAX.DELETE.COMPLETED]: (state: IState, { payload, error }): IState => {
            if (error) {
                return state;
            }
            const currentBrand = state.brand.toJS();
            currentBrand.settings.order_quantity_rules = payload;
            const brand = new Brand(currentBrand);
            const newState = { ...state, brand };
            // const newState = (Object as any).assign({}, state, { brand });
            ajaxConfig.scope = newState;
            return newState;
        },
        [BRANDSETTINGS.ORDERMINMAX.FETCH.START]: (state: IState): IState => {
            return { ...state, fetchingMinMaxRules: true };
        },
        [BRANDSETTINGS.ORDERMINMAX.FETCH.COMPLETED]: (state: IState, { payload, error }): IState => {
            if (error) {
                if (Validators.objectId(payload)) {
                    // we asked got a 404, need to handle slightly differently
                    const currentBrand = state.brand.toJS();
                    currentBrand.settings.order_quantity_rules = currentBrand.settings.order_quantity_rules.filter(
                        (rule) => rule._id !== payload,
                    );
                    const brand = new Brand(currentBrand);
                    const newState = { ...state, brand, fetchingMinMaxRules: false };
                    ajaxConfig.scope = newState;
                    return newState;
                } else {
                    const currentBrand = state.brand.toJS();
                    const brand = new Brand(currentBrand);
                    const newState = { ...state, brand, fetchingMinMaxRules: false };
                    ajaxConfig.scope = newState;
                    return newState;
                }
            } else {
                const currentBrand = state.brand.toJS();
                const brand = new Brand(currentBrand);
                const newState = { ...state, brand, fetchingMinMaxRules: false };
                ajaxConfig.scope = newState;
                return newState; // we fetched an id we already have
            }
        },
        [BRANDSETTINGS.PREFERREDRETAILERS.FETCH.START]: (state: IState): IState => {
            return { ...state, fetchingPreferredRetailers: true };
        },
        [BRANDSETTINGS.PREFERREDRETAILERS.FETCH.COMPLETED]: (state: IState, { payload, error }): IState => {
            const newState = {
                ...state,
                fetchingPreferredRetailers: false,
                preferredRetailers: error ? [] : payload.retailers,
                preferredRetailersError: error,
            };
            ajaxConfig.scope = newState;
            return newState;
        },

        [BRANDSETTINGS.PREFERREDRETAILERS.UPDATE.START]: (state: IState): IState => {
            return { ...state, fetchingPreferredRetailers: true };
        },
        [BRANDSETTINGS.PREFERREDRETAILERS.UPDATE.COMPLETED]: (state: IState, { payload }): IState => {
            const currentBrand = state.brand.toJS();
            currentBrand.modules.preferred_retailers = payload.modules.preferred_retailers || [];

            const brand = new Brand(currentBrand);
            const newState = {
                ...state,
                brand,
                fetchingPreferredRetailers: false,
            };
            ajaxConfig.scope = newState;
            return newState;
        },
        [SCOPE.COMPANIES.START]: (state: IState, { payload }): IState => {
            const { meta } = payload;
            const newState = {
                ...state,
                fetchingStartOrderCompanies: true,
            };
            if (meta && meta.replace) {
                newState.startOrderCompanies = [];
            }
            return newState;
        },
        [SCOPE.COMPANIES.COMPLETED]: (state: IState, { payload, error, meta }): IState => {
            if (error) {
                return state;
            }

            return {
                ...state,
                currentCompanies: payload.currentCompanies,
                startOrderCompanies: meta.replace ? payload.results : state.startOrderCompanies.concat(payload.results),
                fetchingStartOrderCompanies: false,
                nextPageStartOrderCompanies: payload.nextPage,
            };
        },
        [SCOPE_COMPANY.SUCCESS]: (state: IState, { payload }): IState => ({
            ...state,
            company: payload,
        }),
        [SCOPE_HIDE_MSRP]: (state: IState, { payload }): IState => {
            return {
                ...state,
                hideMsrp: payload,
            };
        },
        [SCOPE_HIDE_WHOLESALE]: (state: IState, { payload }): IState => {
            return {
                ...state,
                hideWholesale: payload,
            };
        },
        [SCOPE_IS_CHILD_FRAME_SET]: (state: IState, { payload: isChildFrame }): IState => ({
            ...state,
            isChildFrame,
        }),
        [BRANDSETTINGS.IMAGE_FORMATS.UPDATE.START]: (state: IState) => {
            return {
                ...state,
                isUpdating: true,
            };
        },
        [BRANDSETTINGS.IMAGE_FORMATS.UPDATE.ERROR]: (state: IState) => {
            return {
                ...state,
                isUpdating: true,
            };
        },

        [BRANDSETTINGS.IMAGE_FORMATS.UPDATE.SUCCESS]: (state: IState, { payload }): IState => {
            const currentBrand = state.brand.toJS();
            currentBrand.settings = { ...currentBrand.settings, ...payload.settings };

            const brand = new Brand(currentBrand);
            const newState = { ...state, brand, isUpdating: false };
            ajaxConfig.scope = newState;
            return newState;
        },
    } as any,
    initialState,
);
